import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment-timezone';

import { DATE_FORMAT } from '../../../constants';
import { citysprintApis } from '../../../apis';
import { CitysprintHelpers } from '../../../helpers';
import { NumberUtil } from '../../../utils';
import { buildCitysprintCheckoutPayload } from '../helpers';
import { CitysprintWizardActions } from './slice';

export const enterCitysprintWizardFromQuickQuote =
  (formValues, networkSelection) => dispatch => {
    const seed = CitysprintHelpers.buildCitysprintStateFromQuickQuote(
      formValues,
      networkSelection
    );
    const { delivery, collection, prefetchedVehicleKey, selectedNetworkCode } =
      seed;

    dispatch(CitysprintWizardActions.clear());
    dispatch(
      CitysprintWizardActions.patchParcelStep({
        delivery,
        collection,
      })
    );
    if (prefetchedVehicleKey || selectedNetworkCode) {
      dispatch(
        CitysprintWizardActions.setQuickQuote({
          prefetchedVehicleKey,
          selectedNetworkCode,
        })
      );
    }
  };

export const lockCitysprintQuoteSession = createAsyncThunk(
  'citysprintWizard/lockQuoteSession',
  async (parcelStep, { rejectWithValue }) => {
    const {
      collection,
      delivery,
      collectionDate,
      timeHour,
      timeMinute,
      selectedQuote,
    } = parcelStep;
    const hh = String(timeHour ?? '').padStart(2, '0');
    const mm = String(timeMinute ?? '').padStart(2, '0');
    const collectionDateTime = moment
      .tz(
        `${collectionDate} ${hh}:${mm}:00`,
        `${DATE_FORMAT.YYYY_MM_DD_FORMAT} HH:mm:ss`,
        DATE_FORMAT.EUROPE_LONDON_TIME_ZONE
      )
      .toISOString();
    try {
      const response = await citysprintApis.createQuoteSession({
        collectionPostcode: collection?.postcode,
        deliveryPostcode: delivery?.postcode,
        collectionDateTime,
        serviceCode: selectedQuote?.serviceCode,
        vehicleType: selectedQuote?.vehicleType,
        vehicleDescription: selectedQuote?.vehicleDescription,
        totalBookingPrice: NumberUtil.roundToTwoDecimals(
          selectedQuote?.prices?.incl
        ),
      });
      const responseData = response?.data ?? response;
      if (!responseData?.success) {
        return rejectWithValue('Invalid quote session response');
      }
      return responseData;
    } catch (error) {
      return rejectWithValue(error?.message || 'Quote session API error');
    }
  }
);

export const validateCitysprintQuoteSession = createAsyncThunk(
  'citysprintWizard/validateQuoteSession',
  async (_, { rejectWithValue }) => {
    try {
      await citysprintApis.validateQuoteSession();
      return { valid: true };
    } catch (error) {
      return rejectWithValue(error?.message || 'Session validation failed');
    }
  }
);

export const checkoutCitysprintBooking = createAsyncThunk(
  'citysprintWizard/checkout',
  // eslint-disable-next-line complexity -- small thunk
  async ({ paymentNonce, mergedData }, { rejectWithValue }) => {
    try {
      const response = await citysprintApis.checkout(
        buildCitysprintCheckoutPayload(mergedData, paymentNonce)
      );
      const envelope = response?.data ?? response;
      const result = envelope?.data ?? envelope;
      const bookingId =
        result?.bookingId != null && String(result.bookingId).trim() !== ''
          ? String(result.bookingId).trim()
          : null;
      const oobCode =
        result?.oobCode != null && String(result.oobCode).trim() !== ''
          ? String(result.oobCode).trim()
          : null;
      if (!bookingId) {
        return rejectWithValue('Missing booking id after checkout.');
      }
      if (!oobCode) {
        return rejectWithValue('Missing invoice token after checkout.');
      }
      return { bookingId, oobCode };
    } catch (error) {
      return rejectWithValue(error?.message || 'Checkout failed');
    }
  }
);
